/**
 * ListOrganizationModal — the "list your organization" form for Connect to Care.
 *
 * Replaces a `mailto:` on the CTA at the foot of a scenario's resource list. A
 * mailto is a dead end for most visitors (no configured client, or a webmail
 * protocol handler that hijacks it to an unrelated domain), which is a poor way
 * to treat the one CTA aimed at people volunteering to help patients.
 *
 * Inline modal rather than a route on purpose: the CTA sits under a resource list
 * the visitor is already reading for a specific diagnosis. Navigating away loses
 * that context — and the scenario is exactly the routing signal we want, so it is
 * captured with the submission rather than asked for.
 *
 * Writes straight to Supabase like Contact.tsx does; the table's RLS allows anon
 * INSERT and nothing else, so there is no endpoint to stand up.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { supabase } from "@/integrations/supabase/client";
import { X, Send, CheckCircle2, Building2 } from "lucide-react";

const serif = "'Instrument Serif', Georgia, serif";
const sans = "'Instrument Sans', system-ui, -apple-system, sans-serif";

// Presets rather than a free number: an org that picks "50 miles" is giving us a
// figure we can match against a visitor's ZIP. "Statewide"/"a few counties" is not.
const RADII = [10, 25, 50, 100, 150, 250];

const ORG_TYPES = [
  "Non-profit / charity",
  "Patient advocacy group",
  "Financial assistance program",
  "Support group",
  "Government / public program",
  "Clinic or provider",
  "Other",
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  isDark: boolean;
  scenarioSlug?: string;
  scenarioName?: string;
}

type State = { kind: "idle" | "sending" | "sent" } | { kind: "error"; message: string };

export function ListOrganizationModal({
  isOpen, onClose, isDark, scenarioSlug, scenarioName,
}: Props) {
  const [state, setState] = useState<State>({ kind: "idle" });
  const [f, setF] = useState<Record<string, string>>({});
  const [national, setNational] = useState(false);
  const firstFieldRef = useRef<HTMLInputElement>(null);

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setF(p => ({ ...p, [k]: e.target.value }));

  // Esc closes, and focus lands in the first field so the form is usable from the
  // keyboard the moment it opens.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const t = setTimeout(() => firstFieldRef.current?.focus(), 60);
    return () => { window.removeEventListener("keydown", onKey); clearTimeout(t); };
  }, [isOpen, onClose]);

  // Reset on close so reopening never shows a stale success screen or old input.
  useEffect(() => {
    if (!isOpen) { setState({ kind: "idle" }); setF({}); setNational(false); }
  }, [isOpen]);

  if (!isOpen) return null;

  // Service area is required, one way or the other. Mirrors the table's CHECK
  // constraint — better to grey the button out than to let PostgREST reject the
  // insert and show a generic failure.
  const areaOk = national || !!f.service_location?.trim();

  const canSubmit =
    !!f.org_name?.trim() &&
    !!f.contact_email?.trim() &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.contact_email.trim()) &&
    areaOk &&
    state.kind !== "sending";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setState({ kind: "sending" });
    try {
      // Cast: src/integrations/supabase/types.ts is generated and does not yet know
      // about organization_listing_requests (added 2026-08-02,
      // migrations/create_organization_listing_requests.sql). Regenerating the types
      // drops this cast — the insert itself is correct and RLS-guarded.
      const { error } = await (supabase as any).from("organization_listing_requests").insert({
        org_name: f.org_name.trim(),
        org_type: f.org_type || null,
        website: f.website?.trim() || null,
        contact_name: f.contact_name?.trim() || null,
        contact_email: f.contact_email.trim().toLowerCase(),
        contact_phone: f.contact_phone?.trim() || null,
        services: f.services?.trim() || null,
        serves_nationally: national,
        service_location: national ? null : (f.service_location?.trim() || null),
        service_radius_miles: national ? null : Number(f.service_radius_miles || 50),
        scenario_slug: scenarioSlug || null,
        scenario_name: scenarioName || null,
        message: f.message?.trim() || null,
        metadata: { referrer: typeof window !== "undefined" ? window.location.pathname : null },
      });
      if (error) throw error;
      setState({ kind: "sent" });
    } catch (err) {
      console.error("[ListOrganizationModal] insert failed:", err);
      setState({
        kind: "error",
        message: "We couldn't submit that. Please try again in a moment.",
      });
    }
  };

  const gold = "var(--civic-gold)";
  const navy = "var(--civic-navy)";
  const bg = isDark ? "#0d1628" : "#ffffff";
  const fg = isDark ? "#e5e7eb" : "var(--civic-ink)";
  const muted = isDark ? "#9ca3af" : "#6b7280";
  const border = isDark ? "rgba(var(--civic-gold-rgb),0.25)" : "rgba(var(--civic-navy-rgb),0.18)";
  const accent = isDark ? gold : navy;

  const field: React.CSSProperties = {
    width: "100%", padding: "9px 11px", borderRadius: 6, fontFamily: sans, fontSize: 14,
    background: isDark ? "rgba(255,255,255,0.04)" : "#fff",
    border: `1px solid ${border}`, color: fg, outline: "none",
  };
  const label: React.CSSProperties = {
    display: "block", fontSize: 12, fontFamily: sans, color: muted, marginBottom: 4, fontWeight: 600,
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="List your organization"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9999, display: "flex",
        alignItems: "center", justifyContent: "center", padding: 16,
        background: "rgba(0,8,24,0.72)", backdropFilter: "blur(3px)",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: bg, borderRadius: 12, border: `1px solid ${border}`,
          width: "100%", maxWidth: 560, maxHeight: "90vh", overflowY: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
        }}
      >
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 18px", borderBottom: `1px solid ${border}`,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Building2 className="w-4 h-4" style={{ color: accent }} />
            <span style={{ fontFamily: serif, fontSize: 19, color: fg }}>
              List your organization
            </span>
          </div>
          <button onClick={onClose} aria-label="Close"
            style={{ background: "none", border: "none", cursor: "pointer", color: muted, padding: 4 }}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {state.kind === "sent" ? (
          <div style={{ padding: "34px 24px", textAlign: "center" }}>
            <CheckCircle2 className="w-10 h-10" style={{ color: "#2E7D4F", margin: "0 auto 12px" }} />
            <p style={{ fontFamily: serif, fontSize: 21, color: fg, margin: "0 0 8px" }}>
              Thank you — we have it.
            </p>
            <p style={{ fontFamily: sans, fontSize: 14, color: muted, margin: "0 0 20px", lineHeight: 1.6 }}>
              We review every listing by hand to make sure patients only see organizations that
              genuinely help. If we need anything else we'll email you. There is no fee, ever.
            </p>
            <button onClick={onClose} style={{
              padding: "9px 22px", borderRadius: 6, cursor: "pointer", fontFamily: sans,
              fontSize: 14, fontWeight: 600, background: accent, color: "#fff", border: "none",
            }}>Close</button>
          </div>
        ) : (
          <form onSubmit={submit} style={{ padding: 18 }}>
            <p style={{ fontFamily: sans, fontSize: 13.5, color: muted, margin: "0 0 16px", lineHeight: 1.6 }}>
              Listings are free and always will be. We review each one by hand.
              {scenarioName && (
                <> We'll note that you found us on <strong style={{ color: fg }}>{scenarioName}</strong>.</>
              )}
            </p>

            <div style={{ display: "grid", gap: 12 }}>
              <div>
                <label style={label} htmlFor="org_name">Organization name *</label>
                <input id="org_name" ref={firstFieldRef} required style={field}
                  value={f.org_name || ""} onChange={set("org_name")} />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={label} htmlFor="org_type">Type</label>
                  <select id="org_type" style={field} value={f.org_type || ""} onChange={set("org_type")}>
                    <option value="">Select…</option>
                    {ORG_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label style={label} htmlFor="website">Website</label>
                  <input id="website" style={field} placeholder="https://"
                    value={f.website || ""} onChange={set("website")} />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={label} htmlFor="contact_name">Your name</label>
                  <input id="contact_name" style={field}
                    value={f.contact_name || ""} onChange={set("contact_name")} />
                </div>
                <div>
                  <label style={label} htmlFor="contact_email">Email *</label>
                  <input id="contact_email" type="email" required style={field}
                    value={f.contact_email || ""} onChange={set("contact_email")} />
                </div>
              </div>

              <div>
                <label style={label} htmlFor="services">What do you help patients with?</label>
                <textarea id="services" rows={3} style={{ ...field, resize: "vertical" }}
                  placeholder="Financial assistance, transport, lodging, navigation, legal aid…"
                  value={f.services || ""} onChange={set("services")} />
              </div>

              {/* Service area. Was a single free-text box ("National, or e.g. TX, OK,
                  NM") — unqueryable, so it could only ever be read by a human at
                  review time. An origin plus a radius can be geocoded and matched
                  against a visitor's location, which is what lets these listings be
                  surfaced to the patients they can actually reach. */}
              <div>
                <label style={label}>Where do you serve? *</label>
                <label style={{
                  display: "flex", alignItems: "center", gap: 8, marginBottom: 10,
                  fontFamily: sans, fontSize: 13.5, color: fg, cursor: "pointer",
                }}>
                  <input type="checkbox" checked={national}
                    onChange={e => setNational(e.target.checked)}
                    style={{ width: 15, height: 15, accentColor: accent, cursor: "pointer" }} />
                  We serve patients nationwide
                </label>

                {!national && (
                  <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 12 }}>
                    <div>
                      <label style={{ ...label, fontWeight: 400 }} htmlFor="service_location">
                        ZIP code or city
                      </label>
                      <input id="service_location" style={field} placeholder="75201 or Dallas, TX"
                        value={f.service_location || ""} onChange={set("service_location")} />
                    </div>
                    <div>
                      <label style={{ ...label, fontWeight: 400 }} htmlFor="service_radius_miles">
                        Radius served
                      </label>
                      <select id="service_radius_miles" style={field}
                        value={f.service_radius_miles || "50"} onChange={set("service_radius_miles")}>
                        {RADII.map(r => (
                          <option key={r} value={r}>{r} miles</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {state.kind === "error" && (
              <p style={{ fontFamily: sans, fontSize: 13, color: "#e05a5a", margin: "12px 0 0" }}>
                {state.message}
              </p>
            )}

            <button type="submit" disabled={!canSubmit} style={{
              marginTop: 16, width: "100%", padding: "11px 18px", borderRadius: 6,
              cursor: canSubmit ? "pointer" : "not-allowed", fontFamily: sans, fontSize: 14.5,
              fontWeight: 600, border: "none", color: "#fff",
              background: canSubmit ? accent : (isDark ? "#334155" : "#c9ccd4"),
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}>
              <Send className="w-4 h-4" />
              {state.kind === "sending" ? "Submitting…" : "Submit listing request"}
            </button>

            <p style={{ fontFamily: sans, fontSize: 11.5, color: muted, margin: "10px 0 0", textAlign: "center" }}>
              * required. We never sell or share your details.
            </p>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
}
